import React, { useState } from 'react';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  Box,
  Checkbox,
  makeStyles,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLocation, Link as RouteLink } from 'react-router-dom';

import { useProfileContext } from '../../core/profile';
import { Contact as ContactClass, Who as WhoClass } from '../../core/types';

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  subTitle: {
    marginBottom: theme.spacing(3),
  },
  requiredText: {
    marginBottom: theme.spacing(3),
  },
  input: {
    marginBottom: theme.spacing(2),
    maxWidth: '500px',
  },
  mobile: {
    marginBottom: theme.spacing(3),
    maxWidth: '500px',
  },
  checkbox: {
    padding: 0,
    paddingRight: theme.spacing(1),
  },
  link: {
    color: theme.palette.primary.main,
    '&:focus': {
      color: theme.palette.primary.dark,
    },
  },
}));

const ContactDetails = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const location: any = useLocation();
  const { useProfile, onChange, fieldError } = useProfileContext();
  const onChangeContact = onChange('contact');
  const onChangeWho = onChange('who');
  const data = useProfile<ContactClass>('contact');
  const whoData = useProfile<WhoClass>('who');
  const { email, mobile, signedTermsAndConditions, wishesContact } = data;
  const { name } = whoData;
  const [isTouched, setIsTouched] = useState({
    name: false,
    email: false,
    mobile: false,
    signedTermsAndConditions: false,
  });

  return (
    <Box className={classes.body}>
      {location.state?.type === 'existingAccountError' && (
        <Box my={2}>
          <Alert severity='error'>
            <AlertTitle>{t('contact.errors.existingUser.title')}</AlertTitle>
            {t('contact.errors.existingUser.text')}{' '}
            <RouteLink to={{ pathname: '/', state: { type: 'prefillEmail' } }}>
              {t('contact.errors.existingUser.link')}
            </RouteLink>
          </Alert>
        </Box>
      )}
      {location.state?.type === 'existingDossierError' && (
        <Box my={2}>
          <Alert severity='error'>
            <AlertTitle>{t('contact.errors.existingDossier.title')}</AlertTitle>
            {t('contact.errors.existingDossier.text')}
          </Alert>
        </Box>
      )}
      <Typography variant='h5' className={classes.title}>
        {t('contactPage.title')}
      </Typography>
      <Typography
        variant='body2'
        color={'textSecondary'}
        className={classes.subTitle}>
        {t('contactPage.subtitle')}
      </Typography>
      <Typography
        variant='caption'
        color={'textSecondary'}
        className={classes.requiredText}>
        {t('requiredText')}
      </Typography>
      <TextField
        required
        name='name'
        label={t('form.name')}
        variant='outlined'
        value={name}
        onBlur={() =>
          !isTouched.name && setIsTouched(state => ({ ...state, name: true }))
        }
        error={isTouched.name && fieldError('who', 'name')}
        helperText={isTouched.name && fieldError('who', 'name')}
        onChange={event => onChangeWho({ name: event.target.value })}
        className={classes.input}
      />
      <TextField
        required
        name='email'
        label={t('E-mailadres')}
        variant='outlined'
        value={email}
        onBlur={() => {
          if (!isTouched.email) {
            setIsTouched(state => ({ ...state, email: true }));
          }
          // we need to update the who field on email,
          // which will be shown after we get the result
          onChangeWho({ email });
        }}
        error={isTouched.email && fieldError('contact', 'email')}
        helperText={isTouched.email && fieldError('contact', 'email')}
        onChange={event =>
          onChangeContact({ ...data, email: event.target.value })
        }
        className={classes.input}
      />

      <TextField
        required
        name='mobile'
        label={t('form.mobile')}
        type='number'
        variant='outlined'
        value={mobile}
        onBlur={() =>
          !isTouched.mobile &&
          setIsTouched(state => ({ ...state, mobile: true }))
        }
        onChange={event =>
          onChangeContact({ ...data, mobile: event.target.value })
        }
        className={classes.mobile}
      />
      <Box display='flex' mb={1}>
        <Box display='flex' alignItems='center'>
          <Checkbox
            checked={!!wishesContact}
            onChange={event =>
              onChangeContact({
                ...data,
                wishesContact: !wishesContact,
              })
            }
            className={classes.checkbox}
            color='primary'
          />
        </Box>
        <Box display='flex' alignItems='center'>
          <Typography variant='body2'>{t('checkbox.wishesContact')}</Typography>
        </Box>
      </Box>
      <Box display='flex'>
        <Box display='flex' alignItems='center'>
          <Checkbox
            checked={signedTermsAndConditions}
            onBlur={() =>
              !isTouched.signedTermsAndConditions &&
              setIsTouched(state => ({
                ...state,
                signedTermsAndConditions: true,
              }))
            }
            onChange={event => {
              onChangeContact({
                ...data,
                signedTermsAndConditions: !signedTermsAndConditions,
              });
            }}
            className={classes.checkbox}
            color='primary'
          />
        </Box>
        <Box display='flex' alignItems='center'>
          <Typography variant='body2'>
            {t('checkbox.termsAndConditions1')}{' '}
            <Link
              target='_blank'
              rel='noreferrer noopener'
              className={classes.link}
              href='https://www.myfaro.be/#modal_terms_and_conditions'>
              {t('checkbox.termsAndConditions2')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactDetails;
